import {
	AppstoreOutlined,
	ControlOutlined,
	FileTextOutlined,
	ReloadOutlined,
	SearchOutlined,
	FilterOutlined,
	UnorderedListOutlined,
	CaretRightOutlined,
} from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Divider, Input, Skeleton } from "antd";
import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import ButtonDropdown from "../../components/Button/ButtonDropdown";
import CheckboxDropdown from "../../components/CheckboxDropdown";
import DefaultContainer from "../../components/DefaultContainer";
import ReportingFormDrawerV2 from "../../components/Reporting/ReportingFormDrawerV2";
import TabButton from "../../components/TabButton";
import MyReports from "../../containers/MyReports";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { refreshControlCenter } from "../../services/api-server/control_center";
import { useReportingHubData } from "../../hooks/useReportingHubData";
import { useReportingHubFilterAssets } from "../../hooks/useReportingHubFilterAssets";
import {
	applyReportingHubFilters,
	buildLookupMaps,
	buildOptions,
	cascadeOperationChange,
	cascadeOperatorChange,
	cascadeProjectChange,
	cascadeWellChange,
} from "../../utils/reporting/reportingHubFilters";
import { useTenantStore } from "../../stores/tenantStore";
import { useUserStore } from "../../stores/userStore";
import { TReportType } from "../../types/reporting.types";
import { ControlCenterView } from "../../types/controlCenter.types";
import { GetAntIcon } from "../../utils/ant_icons";
import {
	checkSuperAdminPriviledge,
	includesIgnoreCase,
} from "../../utils/utils";
import {
	CONTROL_CENTER_STATUS_OPTIONS,
	DEFAULT_CONTROL_CENTER_STATUSES,
	DEFAULT_REPORT_STATUSES,
	REPORT_STATUS_OPTIONS,
} from "../../utils/reporting/reportStatusOptions";
import ControlCenter from "./ControlCenter";

const ReportingHubColumn = lazy(() => import("./ReportingHubColumn"));

// Master list of all possible reporting items
const allReportingItems = [
	{
		label: "Audit",
		key: "audit",
		to: "/reporting/audit-stream",
	},
	{
		label: "Incident Report (IR)",
		key: "incident_report",
		to: "/reporting/incident-stream",
	},
	{
		label: "Management of Change (MoC)",
		key: "moc",
		to: "/reporting/moc-stream",
	},
	{
		label: "Opportunity for Improvement (OFI)",
		key: "ofi",
		to: "/reporting/ofi-stream",
	},
	{
		label: "Non-conformance report (NCR)",
		key: "non_conformance",
		to: "/reporting/non-conformance-stream",
	},
];

function HubContent({
	searchValue,
	sortOrder,
	reportingItems,
	selectedOperators,
	selectedReportStatuses,
	selectedProjects,
	selectedWells,
	selectedOperations,
}: {
	searchValue: string;
	sortOrder: 1 | -1;
	reportingItems: typeof allReportingItems;
	selectedOperators: string[];
	selectedReportStatuses: string[];
	selectedProjects: string[];
	selectedWells: string[];
	selectedOperations: string[];
}) {
	return (
		<div className="flex gap-5 h-full">
			{reportingItems.map((item) => (
				<Suspense key={item.key} fallback={<ReportingHubColumnFallback />}>
					<ReportingHubColumn
						type={item.key as TReportType}
						label={item.label}
						searchValue={searchValue}
						sortOrder={sortOrder}
						navigateTo={item.to}
						selectedOperators={selectedOperators}
						selectedReportStatuses={selectedReportStatuses}
						selectedProjects={selectedProjects}
						selectedWells={selectedWells}
						selectedOperations={selectedOperations}
					/>
				</Suspense>
			))}
		</div>
	);
}
const ReportingHubColumnFallback = () => {
	return (
		<div className="flex flex-col gap-2">
			<Skeleton active className="h-full" />
		</div>
	);
};

interface ReportingProps {
	/** The tab to open on, e.g. My reports for /reporting/my-reports. */
	initialTab?: "hub" | "my-reports";
}

function Reporting({ initialTab = "hub" }: ReportingProps) {
	const queryClient = useQueryClient();
	const [activeTab, setActiveTab] = useState<string>(initialTab);
	const [searchValue, setSearchValue] = useState("");
	const [sortOrder, setSortOrder] = useState<1 | -1>(-1);
	const [openDrawer, setOpenDrawer] = useState<boolean>(false);
	const [formType, setFormType] = useState<TReportType | null>(null);
	const [lastUpdatedOn, setLastUpdatedOn] = useState<string | null>(null);
	const [controlCenterLastUpdatedOn, setControlCenterLastUpdatedOn] = useState<
		string | null
	>(null);
	const [controlCenterView, setControlCenterView] =
		useState<ControlCenterView>("report");
	const [filterMode, setFilterMode] = useState(false);
	const [selectedOperators, setSelectedOperators] = useState<string[]>([]);
	const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
	const [selectedWells, setSelectedWells] = useState<string[]>([]);
	const [selectedOperations, setSelectedOperations] = useState<string[]>([]);
	const [selectedReportStatuses, setSelectedReportStatuses] = useState<
		string[]
	>(DEFAULT_REPORT_STATUSES);
	const [ccFilterMode, setCcFilterMode] = useState(false);
	const [ccSelectedOperators, setCcSelectedOperators] = useState<string[]>([]);
	const [ccSelectedProjects, setCcSelectedProjects] = useState<string[]>([]);
	const [ccSelectedWells, setCcSelectedWells] = useState<string[]>([]);
	const [ccSelectedOperations, setCcSelectedOperations] = useState<string[]>(
		[]
	);
	const [ccSelectedReportStatuses, setCcSelectedReportStatuses] = useState<
		string[]
	>(DEFAULT_CONTROL_CENTER_STATUSES);
	const { width: windowWidth } = useWindowDimensions();
	const isSmallScreen = windowWidth < 1024;

	const user = useUserStore((state) => state.user);
	const userRole = useUserStore((state) => state.userRole);
	const currentTenantData = useTenantStore((state) => state.tenant);
	const tenantList = useTenantStore((state) => state.tenantList);

	const operatorOptions = useMemo(() => {
		return tenantList.map((tenant: any) => ({
			label: tenant.name || tenant.role_key,
			value: tenant.role_key,
		}));
	}, [tenantList]);

	const isSuperAdmin = checkSuperAdminPriviledge(userRole);
	const isTenantAdmin = includesIgnoreCase(
		currentTenantData?.tenant_admin,
		user?.email
	);
	const canAccessControlCenter = isSuperAdmin || isTenantAdmin;

	const enabledReportingTypes = useTenantStore(
		(s) => s.tenant?.enabled_reporting_types
	);
	const reportingItems = enabledReportingTypes?.length
		? allReportingItems.filter((item) =>
				enabledReportingTypes.includes(item.key)
			)
		: [];

	const currentTenantKey = currentTenantData?.role_key ?? "";
	const isMasterTenant = useTenantStore((state) => state.isMasterTenant);

	const { allReports } = useReportingHubData(
		reportingItems,
		currentTenantKey,
		isMasterTenant
	);

	const {
		projectMap,
		wellMap,
		operationMap,
		isLoading: isLoadingAssets,
	} = useReportingHubFilterAssets(allReports, currentTenantKey);

	const lookupMaps = useMemo(() => buildLookupMaps(allReports), [allReports]);

	const applySelectionChange = (
		field: "operators" | "projects" | "wells" | "operations",
		nextValues: string[]
	) => {
		const current = {
			operators: selectedOperators,
			projects: selectedProjects,
			wells: selectedWells,
			operations: selectedOperations,
		};

		let next;
		switch (field) {
			case "operators":
				next = cascadeOperatorChange(nextValues, current, lookupMaps);
				break;
			case "projects":
				next = cascadeProjectChange(nextValues, current, lookupMaps);
				break;
			case "wells":
				next = cascadeWellChange(nextValues, current, lookupMaps);
				break;
			case "operations":
				next = cascadeOperationChange(nextValues, current, lookupMaps);
				break;
		}

		setSelectedOperators(next.operators);
		setSelectedProjects(next.projects);
		setSelectedWells(next.wells);
		setSelectedOperations(next.operations);
	};

	const baseReports = useMemo(
		() =>
			applyReportingHubFilters(allReports, {
				operators: selectedOperators,
			}),
		[allReports, selectedOperators]
	);

	const projectOptions = useMemo(
		() => buildOptions(baseReports, "project", projectMap, "project_name"),
		[baseReports, projectMap]
	);

	const projectFilteredReports = useMemo(
		() =>
			applyReportingHubFilters(baseReports, {
				projects: selectedProjects,
			}),
		[baseReports, selectedProjects]
	);

	const wellOptions = useMemo(
		() => buildOptions(projectFilteredReports, "well", wellMap, "well_name"),
		[projectFilteredReports, wellMap]
	);

	const wellFilteredReports = useMemo(
		() =>
			applyReportingHubFilters(projectFilteredReports, {
				wells: selectedWells,
			}),
		[projectFilteredReports, selectedWells]
	);

	const operationOptions = useMemo(
		() =>
			buildOptions(
				wellFilteredReports,
				"operation",
				operationMap,
				"operation_name"
			),
		[wellFilteredReports, operationMap]
	);

	const ccLookupMaps = useMemo(() => buildLookupMaps(allReports), [allReports]);

	const ccBaseReports = useMemo(
		() =>
			applyReportingHubFilters(allReports, {
				operators: ccSelectedOperators,
			}),
		[allReports, ccSelectedOperators]
	);

	const ccProjectOptions = useMemo(
		() => buildOptions(ccBaseReports, "project", projectMap, "project_name"),
		[ccBaseReports, projectMap]
	);

	const ccProjectFilteredReports = useMemo(
		() =>
			applyReportingHubFilters(ccBaseReports, {
				projects: ccSelectedProjects,
			}),
		[ccBaseReports, ccSelectedProjects]
	);

	const ccWellOptions = useMemo(
		() => buildOptions(ccProjectFilteredReports, "well", wellMap, "well_name"),
		[ccProjectFilteredReports, wellMap]
	);

	const ccWellFilteredReports = useMemo(
		() =>
			applyReportingHubFilters(ccProjectFilteredReports, {
				wells: ccSelectedWells,
			}),
		[ccProjectFilteredReports, ccSelectedWells]
	);

	const ccOperationOptions = useMemo(
		() =>
			buildOptions(
				ccWellFilteredReports,
				"operation",
				operationMap,
				"operation_name"
			),
		[ccWellFilteredReports, operationMap]
	);

	const applyCcSelectionChange = (
		field: "operators" | "projects" | "wells" | "operations",
		nextValues: string[]
	) => {
		const current = {
			operators: ccSelectedOperators,
			projects: ccSelectedProjects,
			wells: ccSelectedWells,
			operations: ccSelectedOperations,
		};

		let next;
		switch (field) {
			case "operators":
				next = cascadeOperatorChange(nextValues, current, ccLookupMaps);
				break;
			case "projects":
				next = cascadeProjectChange(nextValues, current, ccLookupMaps);
				break;
			case "wells":
				next = cascadeWellChange(nextValues, current, ccLookupMaps);
				break;
			case "operations":
				next = cascadeOperationChange(nextValues, current, ccLookupMaps);
				break;
		}

		setCcSelectedOperators(next.operators);
		setCcSelectedProjects(next.projects);
		setCcSelectedWells(next.wells);
		setCcSelectedOperations(next.operations);
	};

	const hasActiveFilters =
		selectedOperators.length > 0 ||
		selectedProjects.length > 0 ||
		selectedWells.length > 0 ||
		selectedOperations.length > 0 ||
		selectedReportStatuses.length > 0;

	const hasActiveCcFilters =
		ccSelectedOperators.length > 0 ||
		ccSelectedProjects.length > 0 ||
		ccSelectedWells.length > 0 ||
		ccSelectedOperations.length > 0 ||
		ccSelectedReportStatuses.length > 0;

	const handleSortOrder = useCallback(() => {
		setSortOrder((prev) => (prev === 1 ? -1 : 1));
	}, []);

	const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchValue(e.target.value);
	}, []);

	return (
		<>
			<DefaultContainer
				headerContent={
					<div className="flex flex-nowrap justify-between items-center gap-2.5 w-full">
						<div className="flex items-center gap-2.5 min-w-0 overflow-hidden flex-shrink">
							<TabButton
								identifier="hub"
								label="Hub"
								icon={<AppstoreOutlined />}
								activePage={activeTab}
								setActivePage={setActiveTab}
								showLabel={!isSmallScreen}
							/>
							<TabButton
								identifier="my-reports"
								label="My reports"
								icon={<FileTextOutlined />}
								activePage={activeTab}
								setActivePage={setActiveTab}
								showLabel={!isSmallScreen}
							/>
							{canAccessControlCenter && (
								<TabButton
									identifier="control-center"
									label="Control center"
									icon={<ControlOutlined />}
									activePage={activeTab}
									setActivePage={setActiveTab}
									showLabel={!isSmallScreen}
								/>
							)}
							{activeTab === "control-center" && canAccessControlCenter && (
								<>
									<CaretRightOutlined />
									<TabButton
										identifier="task"
										label="Task view"
										icon={<UnorderedListOutlined />}
										activePage={controlCenterView}
										setActivePage={(v) =>
											setControlCenterView(v as ControlCenterView)
										}
										showLabel={!isSmallScreen}
									/>
									<TabButton
										identifier="report"
										label="Report view"
										icon={<FileTextOutlined />}
										activePage={controlCenterView}
										setActivePage={(v) =>
											setControlCenterView(v as ControlCenterView)
										}
										showLabel={!isSmallScreen}
									/>
								</>
							)}
							{activeTab === "my-reports" && lastUpdatedOn && (
								<div className="flex items-center gap-2 ml-4 min-w-0 overflow-hidden">
									<span
										className="text-gray-500 truncate"
										title={`Last updated on ${lastUpdatedOn}`}
									>
										Last updated on {lastUpdatedOn}
									</span>
									<Button
										type="link"
										className="text-gray-500! p-0! flex-shrink-0"
										icon={<ReloadOutlined />}
										onClick={() => {
											queryClient.refetchQueries({ queryKey: ["my_reports"] });
										}}
										title="Refresh"
									/>
								</div>
							)}
							{activeTab === "control-center" && controlCenterLastUpdatedOn && (
								<div className="flex items-center gap-2 ml-4 min-w-0 overflow-hidden">
									<span
										className="text-gray-500 truncate"
										title={`Last updated on ${controlCenterLastUpdatedOn}`}
									>
										Last updated on {controlCenterLastUpdatedOn}
									</span>
									<Button
										type="link"
										className="text-gray-500! p-0! flex-shrink-0"
										icon={<ReloadOutlined />}
										onClick={async () => {
											try {
												await refreshControlCenter(controlCenterView);
											} catch (error) {
												console.warn(
													"Failed to refresh control center:",
													error
												);
											}
											queryClient.invalidateQueries({
												queryKey: ["control_center_counts", controlCenterView],
											});
										}}
										title="Refresh"
									/>
								</div>
							)}
						</div>
						<div className="flex justify-end items-center gap-2.5">
							<Input
								placeholder="Search"
								className="input-clearable-panel"
								allowClear
								prefix={<SearchOutlined />}
								onChange={handleSearch}
							/>
							{(activeTab === "hub" || activeTab === "control-center") && (
								<div
									className={`cursor-pointer px-2.5 py-1 ${
										(activeTab === "hub" && hasActiveFilters) ||
										(activeTab === "control-center" && hasActiveCcFilters)
											? "project-active-button"
											: ""
									}`}
									onClick={() => {
										if (activeTab === "hub") {
											setFilterMode(!filterMode);
										} else {
											setCcFilterMode(!ccFilterMode);
										}
									}}
								>
									<FilterOutlined />
								</div>
							)}
							<div
								className="cursor-pointer px-2.5 py-1"
								onClick={handleSortOrder}
							>
								{GetAntIcon(sortOrder === 1 ? "descending" : "ascending")}
							</div>
							<>
								<Divider
									type="vertical"
									className="mx-0! h-6! border-(--primary-text)/30!"
								/>
								<ButtonDropdown
									buttonType="link"
									icon={GetAntIcon("plus")}
									iconPosition="start"
									label={isSmallScreen ? "" : "Add"}
									items={reportingItems}
									onClick={(item) => {
										setOpenDrawer(true);
										setFormType(item.key);
									}}
								/>
							</>
						</div>
					</div>
				}
			>
				{activeTab === "hub" && filterMode && (
					<div className="filter-area show-filter left-0 top-0 sticky mb-4">
						<CheckboxDropdown
							options={operatorOptions}
							onChange={(values) => applySelectionChange("operators", values)}
							onClear={() => applySelectionChange("operators", [])}
							defaultValue={selectedOperators}
							Key="operator"
							label="Operator"
							hideArrow={false}
						/>
						<CheckboxDropdown
							options={projectOptions}
							onChange={(values) => applySelectionChange("projects", values)}
							onClear={() => applySelectionChange("projects", [])}
							defaultValue={selectedProjects}
							Key="project"
							label="Project"
							hideArrow={false}
						/>
						<CheckboxDropdown
							options={wellOptions}
							onChange={(values) => applySelectionChange("wells", values)}
							onClear={() => applySelectionChange("wells", [])}
							defaultValue={selectedWells}
							Key="well"
							label="Well"
							hideArrow={false}
						/>
						<CheckboxDropdown
							options={operationOptions}
							onChange={(values) => applySelectionChange("operations", values)}
							onClear={() => applySelectionChange("operations", [])}
							defaultValue={selectedOperations}
							Key="operation"
							label="Operation"
							hideArrow={false}
						/>
						<CheckboxDropdown
							options={REPORT_STATUS_OPTIONS}
							onChange={(values) => setSelectedReportStatuses(values)}
							onClear={() => setSelectedReportStatuses(DEFAULT_REPORT_STATUSES)}
							defaultValue={selectedReportStatuses}
							Key="report-status"
							label="Report Status"
							hideArrow={false}
						/>
						<Button
							type="link"
							onClick={() => {
								setSelectedOperators([]);
								setSelectedProjects([]);
								setSelectedWells([]);
								setSelectedOperations([]);
								setSelectedReportStatuses(DEFAULT_REPORT_STATUSES);
							}}
						>
							Reset All
						</Button>
					</div>
				)}
				{activeTab === "control-center" && ccFilterMode && (
					<div className="filter-area show-filter left-0 top-0 sticky mb-4">
						<CheckboxDropdown
							options={operatorOptions}
							onChange={(values) => applyCcSelectionChange("operators", values)}
							onClear={() => applyCcSelectionChange("operators", [])}
							defaultValue={ccSelectedOperators}
							Key="cc-operator"
							label="Operator"
							hideArrow={false}
						/>
						<CheckboxDropdown
							options={ccProjectOptions}
							onChange={(values) => applyCcSelectionChange("projects", values)}
							onClear={() => applyCcSelectionChange("projects", [])}
							defaultValue={ccSelectedProjects}
							Key="cc-project"
							label="Project"
							hideArrow={false}
						/>
						<CheckboxDropdown
							options={ccWellOptions}
							onChange={(values) => applyCcSelectionChange("wells", values)}
							onClear={() => applyCcSelectionChange("wells", [])}
							defaultValue={ccSelectedWells}
							Key="cc-well"
							label="Well"
							hideArrow={false}
						/>
						<CheckboxDropdown
							options={ccOperationOptions}
							onChange={(values) =>
								applyCcSelectionChange("operations", values)
							}
							onClear={() => applyCcSelectionChange("operations", [])}
							defaultValue={ccSelectedOperations}
							Key="cc-operation"
							label="Operation"
							hideArrow={false}
						/>
						<CheckboxDropdown
							options={CONTROL_CENTER_STATUS_OPTIONS}
							onChange={(values) => setCcSelectedReportStatuses(values)}
							onClear={() =>
								setCcSelectedReportStatuses(DEFAULT_CONTROL_CENTER_STATUSES)
							}
							defaultValue={ccSelectedReportStatuses}
							Key="cc-report-status"
							label="Report Status"
							hideArrow={false}
						/>
						<Button
							type="link"
							onClick={() => {
								setCcSelectedOperators([]);
								setCcSelectedProjects([]);
								setCcSelectedWells([]);
								setCcSelectedOperations([]);
								setCcSelectedReportStatuses(DEFAULT_CONTROL_CENTER_STATUSES);
							}}
						>
							Reset All
						</Button>
					</div>
				)}
				<div className="py-5 px-7.5 h-full w-full flex flex-col overflow-x-auto">
					{activeTab === "hub" && (
						<HubContent
							searchValue={searchValue}
							sortOrder={sortOrder}
							reportingItems={reportingItems}
							selectedOperators={selectedOperators}
							selectedReportStatuses={selectedReportStatuses}
							selectedProjects={selectedProjects}
							selectedWells={selectedWells}
							selectedOperations={selectedOperations}
						/>
					)}
					{activeTab === "my-reports" && (
						<MyReports
							onLastUpdated={setLastUpdatedOn}
							searchValue={searchValue}
							sortOrder={sortOrder}
						/>
					)}
					{activeTab === "control-center" && canAccessControlCenter && (
						<div className="overflow-x-auto">
							<div className="inline-block align-top min-w-full">
								<ControlCenter
									searchValue={searchValue}
									sortOrder={sortOrder}
									onLastUpdated={setControlCenterLastUpdatedOn}
									viewMode={controlCenterView}
									selectedOperators={ccSelectedOperators}
									selectedProjects={ccSelectedProjects}
									selectedWells={ccSelectedWells}
									selectedOperations={ccSelectedOperations}
									selectedReportStatuses={ccSelectedReportStatuses}
								/>
							</div>
						</div>
					)}
				</div>
			</DefaultContainer>
			<ReportingFormDrawerV2
				isOpen={openDrawer}
				reportType={formType as TReportType}
				onClose={() => setOpenDrawer(false)}
			/>
		</>
	);
}

export default Reporting;
